"use client";

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useUserMessagesContext } from '@/contexts/UserMessagesContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserCheck, UserX, MessageSquare, UserPlus, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { UserConnection } from '@/hooks/useUserMessages';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface UserConnectionsProps {
  onStartMessaging: (userId: string, userName: string) => void;
}

// Debounce utility function
const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const UserConnections: React.FC<UserConnectionsProps> = ({ onStartMessaging }) => {
  const { connections, messages, unreadMessageCount, requestConnection, updateConnectionStatus, onlineUsers } = useUserMessagesContext();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('connections');

  // --- Add Connection popover state ---
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  const [searchResultUsers, setSearchResultUsers] = useState<{ id: string; full_name: string; username: string; avatar_url?: string }[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string; avatar_url?: string } | null>(null);
  const [adding, setAdding] = useState(false);

  // --- Show all accepted connections involving the current user ---
  const acceptedConnections = useMemo(() => (
    connections.filter(
      c =>
        c.status === 'accepted' &&
        (c.user_id === user?.id || c.connected_user_id === user?.id)
    )
  ), [connections, user]);

  // --- Users you are already connected to or have pending requests with ---
  const connectedUserIds = useMemo(() => {
    const ids = new Set<string>();
    connections.forEach(c => {
      if (c.user_id === user?.id) ids.add(c.connected_user_id);
      else if (c.connected_user_id === user?.id) ids.add(c.user_id);
    });
    if (user?.id) ids.add(user.id); // Exclude self
    return ids;
  }, [connections, user]);

  // Fetch users based on search term with debounce
  useEffect(() => {
    const fetchUsers = async () => {
      if (!searchTerm || searchTerm.length < 3) {
        setSearchResultUsers([]);
        setLoadingUsers(false);
        return;
      }

      setLoadingUsers(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name, username, avatar_url')
          .or(`full_name.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%`) // Search both full_name and username
          .limit(10);

        if (!error && data) {
          const filtered = data.filter(u => !connectedUserIds.has(u.id));
          setSearchResultUsers(filtered);
        } else {
          console.error('Error searching users:', error);
          setSearchResultUsers([]);
        }
      } catch (err) {
        console.error('Exception during user search:', err);
        setSearchResultUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    const debouncedFetch = debounce(fetchUsers, 500);
    if (addPopoverOpen) { // Only search when popover is open
      debouncedFetch();
    } else {
      // Clear search results when popover closes
      setSearchResultUsers([]);
      setSearchTerm('');
      setSelectedUser(null);
    }

    return () => {
      // Cleanup for debounce
    };
  }, [searchTerm, addPopoverOpen, connectedUserIds]);

  // --- Add Connection handler ---
  const handleAddConnection = async (userId: string, userName: string) => {
    setAdding(true);
    try {
      await requestConnection(userId);
      toast({
        title: "Connection request sent",
        description: `A connection request has been sent to ${userName}`,
      });
      setAddPopoverOpen(false);
      setSelectedUser(null);
      setSearchTerm('');
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to send connection request.",
        variant: "destructive"
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">My Connections</h2>
        <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1">
              <UserPlus size={16} />
              Add Connection
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0">
            <div className="p-3 border-b font-semibold flex items-center gap-2">
              <UserPlus className="h-4 w-4 mr-1" />
              Add Connection
            </div>
            <Command>
              <CommandInput
                placeholder="Search users..."
                value={searchTerm}
                onValueChange={setSearchTerm}
                autoFocus
              />
              <CommandList>
                {searchTerm.length < 3 ? (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    Type at least 3 characters to search for users.
                  </div>
                ) : loadingUsers ? (
                  <div className="p-4 text-center text-muted-foreground text-sm">Loading users...</div>
                ) : (
                  <>
                    <CommandEmpty>No users found.</CommandEmpty>
                    <CommandGroup>
                      {searchResultUsers.map(u => (
                        <CommandItem
                          key={u.id}
                          value={u.full_name || u.username || ''}
                          onSelect={() => setSelectedUser({ id: u.id, name: u.full_name || u.username || 'User', avatar_url: u.avatar_url })}
                        >
                          <Avatar className="h-6 w-6 mr-2">
                            {u.avatar_url ? (
                              <AvatarImage src={u.avatar_url} alt={u.full_name || u.username || 'User'} />
                            ) : (
                              <AvatarFallback>{(u.full_name || u.username || 'U').charAt(0)}</AvatarFallback>
                            )}
                          </Avatar>
                          <span>{u.full_name || u.username || 'User'}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
            <div className="p-3 border-t flex justify-end">
              <Button
                size="sm"
                disabled={!selectedUser || adding}
                onClick={() => {
                  if (selectedUser) handleAddConnection(selectedUser.id, selectedUser.name);
                }}
              >
                {adding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
                Add
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {acceptedConnections.length === 0 ? (
        <div className="text-muted-foreground">No connections yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {acceptedConnections.map(conn => {
            // Find the other user's info
            const otherUserId = conn.user_id === user?.id ? conn.connected_user_id : conn.user_id;
            const otherUserName = conn.connected_user_name || 'User';
            const otherUserAvatar = conn.connected_user_avatar || undefined;
            const isOnline = onlineUsers.has(otherUserId);
            console.log(`Checking connected user ${otherUserName} (ID: ${otherUserId}). Is online? ${onlineUsers.has(otherUserId)}`); // <--- ADDED LINE
            return (
              <Card key={conn.id}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar>
                        {otherUserAvatar ? (
                          <AvatarImage src={otherUserAvatar} alt={otherUserName} />
                        ) : (
                          <AvatarFallback>{otherUserName.charAt(0)}</AvatarFallback>
                        )}
                      </Avatar>
                      <span
                        className={cn(
                          'absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full border-2 border-card',
                          isOnline ? 'bg-green-500' : 'bg-gray-400'
                        )}
                        title={isOnline ? 'Online' : 'Offline'}
                      />
                    </div>
                    <CardTitle className="text-md">{otherUserName}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-muted-foreground">
                    Connected on {format(new Date(conn.updated_at), 'PP')}
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onStartMessaging(otherUserId, otherUserName)}
                  >
                    <MessageSquare size={14} className="mr-1" />
                    Message
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserConnections;